// Mastery Service - Precise mastery definition and tracking
// Production Readiness: Implements clear mastery rules with decay logic

import { aiCoach } from './aiCoach';
import { logger } from '../utils/logger';

export interface MasteryStatus {
  mastered: boolean;
  score: number;
  attempts: number;
  average: number;
  decayed?: boolean;
  lastAttemptDate?: string;
}

export interface MasteryRule {
  threshold: number; // 60%
  minAttempts: number; // 2 for average-based mastery
  decayDays: number; // 30 days of no practice
}

/**
 * Mastery Service - Centralized mastery logic
 * 
 * Mastery Rules:
 * 1. Most recent attempt ≥60% → Mastered
 * 2. Average ≥60% with 2+ attempts → Mastered
 * 3. Mastery can decay if no practice for 30+ days
 */
class MasteryService {
  private readonly MASTERY_RULE: MasteryRule = {
    threshold: 60,
    minAttempts: 2,
    decayDays: 30
  };

  /**
   * Check if a test is mastered based on precise rules
   */
  isMastered(testId: string, testScores: Record<string, any>): boolean {
    const score = testScores[testId];
    if (!score) return false;

    // Rule 1: Most recent attempt ≥60%
    if (score.latest !== undefined && score.latest >= this.MASTERY_RULE.threshold) {
      return true;
    }

    // Rule 2: Average ≥60% with 2+ attempts
    if (score.average !== undefined && 
        score.average >= this.MASTERY_RULE.threshold && 
        (score.attempts || 0) >= this.MASTERY_RULE.minAttempts) {
      return true;
    }

    return false;
  }

  /**
   * Check if mastery has decayed due to lack of practice
   * Note: Decay checking requires test history, not just scores
   */
  checkMasteryDecay(testId: string, testScores: Record<string, { average: number; count: number; lastScore: number }>): boolean {
    const score = testScores[testId];
    if (!score) return false;

    // Get test history to check last attempt date
    const testHistory = aiCoach.getTestHistory();
    const lastAttempt = testHistory
      .filter(r => r.testId === testId)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

    if (!lastAttempt) return false;

    const daysSinceLastAttempt = Math.floor(
      (Date.now() - new Date(lastAttempt.date).getTime()) / (1000 * 60 * 60 * 24)
    );

    // Mastery decays if no practice for 30+ days
    if (daysSinceLastAttempt >= this.MASTERY_RULE.decayDays) {
      return true; // Mastery has decayed
    }

    return false;
  }

  /**
   * Get comprehensive mastery status for a test
   */
  getMasteryStatus(testId: string): MasteryStatus {
    const testScores = aiCoach.getTestScores();
    const score = testScores[testId];

    if (!score) {
      return {
        mastered: false,
        score: 0,
        attempts: 0,
        average: 0
      };
    }

    const isMastered = this.isMastered(testId, testScores);
    const hasDecayed = this.checkMasteryDecay(testId, testScores);

    // Get last attempt date from test history
    const testHistory = aiCoach.getTestHistory();
    const lastAttempt = testHistory
      .filter(r => r.testId === testId)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

    return {
      mastered: isMastered && !hasDecayed,
      score: score.lastScore || score.average || 0,
      attempts: score.count || 0,
      average: score.average || 0,
      decayed: hasDecayed,
      lastAttemptDate: lastAttempt?.date
    };
  }

  /**
   * Update mastery status after a test completion
   * Note: aiCoach.getTestScores() returns calculated scores, not mutable objects
   * This method logs mastery status but doesn't modify scores directly
   */
  updateMasteryStatus(testId: string, score: number, _date: string): void {
    // Get current scores (read-only, calculated from history)
    const testScores = aiCoach.getTestScores();
    const _currentScore = testScores[testId];

    // Check if mastery achieved (scores are calculated from test history automatically)
    const masteryStatus = this.getMasteryStatus(testId);
    if (masteryStatus.mastered) {
      logger.debug(`Test ${testId} mastered: ${score}% (${masteryStatus.attempts} attempts)`);
    }
    
    // Note: Test scores are automatically updated by aiCoach when test results are saved
    // This method is for logging and status checking only
  }

  /**
   * Get mastery rule configuration
   */
  getMasteryRule(): MasteryRule {
    return { ...this.MASTERY_RULE };
  }
}

export const masteryService = new MasteryService();
